(function () {
  const CONTAINER_SELECTOR = '[data-explore-grid]';
  const ITEM_SELECTOR = '[data-explore-item]';
  const BUTTON_SELECTOR = '[data-explore-load-more]';
  const SENTINEL_SELECTOR = '[data-explore-sentinel]';
  const TITLE_SELECTOR = '[data-explore-title]';
  const HIDDEN_CLASS = 'is-hidden';
  let resizeTimeout;

  async function refreshExploreGrid(container, handlesToken = '', collectionHandlesToken = '') {
    if (!container || container.dataset.refreshOnLastViewed !== 'true') return;
    if (container.dataset.exploreRefreshInFlight === 'true') return;
    const refreshToken = `${handlesToken || ''}|${collectionHandlesToken || ''}`;
    if (refreshToken && container.dataset.exploreRefreshToken === refreshToken) return;

    const sectionId = container.dataset.sectionId;
    if (!sectionId) return;

    const currentRoot = container.closest(`[data-explore-section-id="${sectionId}"]`);
    if (!currentRoot) return;

    container.dataset.exploreRefreshInFlight = 'true';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      if (handlesToken) {
        url.searchParams.set('lv_handles', handlesToken);
      }
      if (collectionHandlesToken) {
        url.searchParams.set('lv_collection_handles', collectionHandlesToken);
      }

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Unable to refresh explore grid (${response.status})`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = parsed.querySelector(`[data-explore-section-id="${sectionId}"]`);
      if (!nextRoot) return;

      currentRoot.replaceWith(nextRoot);
      initExploreGrids(document);

      const nextContainer = document.querySelector(`${CONTAINER_SELECTOR}[data-section-id="${sectionId}"]`);
      if (nextContainer && refreshToken) {
        nextContainer.dataset.exploreRefreshToken = refreshToken;
      }
    } catch (error) {
      console.error(error);
    } finally {
      container.dataset.exploreRefreshInFlight = 'false';
    }
  }

  function parseNumber(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  function setupExploreGrid(container) {
    if (!container || container.dataset.exploreGridReady === 'true') return;
    container.dataset.exploreGridReady = 'true';

    const items = Array.from(container.querySelectorAll(ITEM_SELECTOR));
    if (!items.length) return;

    const button = container.querySelector(BUTTON_SELECTOR);
    const sentinel = container.querySelector(SENTINEL_SELECTOR);
    const initial = Math.max(parseNumber(container.dataset.initialItems, items.length), 1);
    const batch = Math.max(parseNumber(container.dataset.loadBatch, items.length), 1);
    const enableInfinite = container.dataset.enableInfinite === 'true';

    let visibleCount = Math.min(initial, items.length);
    let observer = null;

    const updateVisibility = () => {
      items.forEach((item, index) => {
        const visible = index < visibleCount;
        item.classList.toggle(HIDDEN_CLASS, !visible);
        item.toggleAttribute('hidden', !visible);
        item.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      const allVisible = visibleCount >= items.length;
      if (button) button.toggleAttribute('hidden', allVisible);
      if (sentinel) sentinel.toggleAttribute('hidden', allVisible || !enableInfinite);

      if (allVisible && observer) {
        observer.disconnect();
        observer = null;
      }

      requestAnimationFrame(() => truncateExploreTitles(container));
    };

    const loadMore = () => {
      if (visibleCount >= items.length) return;
      visibleCount = Math.min(visibleCount + batch, items.length);
      updateVisibility();
    };

    if (button) {
      button.addEventListener('click', loadMore);
    }

    if (enableInfinite && sentinel && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMore();
          }
        });
      });
      observer.observe(sentinel);
    }

    updateVisibility();
  }

  function truncateExploreTitles(scope) {
    if (!scope) return;
    const titleNodes = scope.querySelectorAll(TITLE_SELECTOR);
    if (!titleNodes.length) return;

    titleNodes.forEach((node) => {
      const fullText = (node.dataset.fullText || node.textContent || '').trim();
      if (!fullText) return;

      node.dataset.fullText = fullText;
      node.textContent = fullText;

      if (node.closest('[hidden]')) return;

      const computed = window.getComputedStyle(node);
      const lineHeight = parseFloat(computed.lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

      const ownerContainer = node.closest(CONTAINER_SELECTOR);
      const titleLines = Math.max(parseNumber(ownerContainer?.dataset?.titleLines, 2), 1);
      const maxHeight = lineHeight * titleLines;
      node.style.maxHeight = `${maxHeight}px`;

      if (node.scrollHeight <= maxHeight + 1) return;

      let low = 1;
      let high = fullText.length;
      let best = '..';

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = `${fullText.slice(0, mid).trimEnd()}..`;
        node.textContent = candidate;

        if (node.scrollHeight <= maxHeight + 1) {
          best = candidate;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      node.textContent = best;
    });
  }

  function initExploreGrids(root) {
    const scope = root || document;
    scope.querySelectorAll(CONTAINER_SELECTOR).forEach(setupExploreGrid);
    requestAnimationFrame(() => truncateExploreTitles(scope));
  }

  function replayLastViewedRefresh(root) {
    const scope = root || document;
    const handlesToken = window.__lastViewedHandles ? String(window.__lastViewedHandles) : '';
    const collectionHandlesToken = window.__lastViewedCollectionHandles
      ? String(window.__lastViewedCollectionHandles)
      : '';
    if (!handlesToken && !collectionHandlesToken) return;

    scope.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      refreshExploreGrid(container, handlesToken, collectionHandlesToken);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initExploreGrids(document);
      replayLastViewedRefresh(document);
    });
  } else {
    initExploreGrids(document);
    replayLastViewedRefresh(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initExploreGrids(event.target);
    replayLastViewedRefresh(event.target);
  });

  document.addEventListener('last-viewed:cart-attributes-synced', function (event) {
    const handlesToken = event?.detail?.handles ? String(event.detail.handles) : '';
    const collectionHandlesToken = event?.detail?.collectionHandles
      ? String(event.detail.collectionHandles)
      : '';
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      refreshExploreGrid(container, handlesToken, collectionHandlesToken);
    });
  });

  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(function () {
      initExploreGrids(document);
    }, 120);
  });
})();
